import { z } from "zod"

import { estimateDelay } from "./gccPhat"

export const localizationConfigSchema = z.object({
  sampleRate: z.number().int().min(8_000).max(192_000),
  microphoneCount: z.union([z.literal(2), z.literal(3)]),
  spacingMetres: z.number().min(0.02).max(1),
  temperatureCelsius: z.number().min(-20).max(60),
})

export type LocalizationConfig = z.infer<typeof localizationConfigSchema>

export type DirectionResult =
  | {
      readonly kind: "bearing"
      readonly azimuthDegrees: number
      readonly confidence: number
      readonly mirrored: boolean
      readonly delaysSamples: readonly number[]
    }
  | {
      readonly kind: "insufficient_signal"
      readonly reason: "low_energy" | "ambiguous_peak" | "invalid_delay"
    }

export function estimateDirection(
  channels: readonly Float32Array[],
  config: LocalizationConfig,
): DirectionResult {
  const reference = channels[0]
  const secondary = channels[1]
  if (reference === undefined || secondary === undefined) {
    return { kind: "insufficient_signal", reason: "low_energy" }
  }

  const speedOfSound = 331.3 + 0.606 * config.temperatureCelsius
  const maximumLag = Math.ceil((config.spacingMetres / speedOfSound) * config.sampleRate)
  const secondaryDelay = estimateDelay(reference, secondary, maximumLag)
  if (secondaryDelay.kind === "insufficient_signal") return secondaryDelay

  if (config.microphoneCount === 3) {
    const tertiary = channels[2]
    if (tertiary === undefined) {
      return { kind: "insufficient_signal", reason: "low_energy" }
    }
    const tertiaryDelay = estimateDelay(reference, tertiary, maximumLag)
    if (tertiaryDelay.kind === "insufficient_signal") return tertiaryDelay
    const secondaryDistance = (secondaryDelay.samples * speedOfSound) / config.sampleRate
    const tertiaryDistance = (tertiaryDelay.samples * speedOfSound) / config.sampleRate
    const directionX = secondaryDistance / config.spacingMetres
    const directionY =
      (tertiaryDistance - (config.spacingMetres / 2) * directionX) /
      ((Math.sqrt(3) * config.spacingMetres) / 2)
    const magnitude = Math.hypot(directionX, directionY)
    if (magnitude > 1.2 || magnitude < 0.2) {
      return { kind: "insufficient_signal", reason: "invalid_delay" }
    }
    return {
      kind: "bearing",
      azimuthDegrees: (Math.atan2(directionY, directionX) * 180) / Math.PI,
      confidence: Math.min(secondaryDelay.confidence, tertiaryDelay.confidence),
      mirrored: false,
      delaysSamples: [secondaryDelay.samples, tertiaryDelay.samples],
    }
  }

  const sine = (secondaryDelay.samples * speedOfSound) / (config.sampleRate * config.spacingMetres)
  if (Math.abs(sine) > 1) {
    return { kind: "insufficient_signal", reason: "invalid_delay" }
  }

  return {
    kind: "bearing",
    azimuthDegrees: (Math.asin(sine) * 180) / Math.PI,
    confidence: secondaryDelay.confidence,
    mirrored: true,
    delaysSamples: [secondaryDelay.samples],
  }
}
